import React, { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import Markdown, { type Components, type ExtraProps } from 'react-markdown';

import classNames from 'classnames';
import remarkGfm from 'remark-gfm';
import { remarkHighlightMark } from 'remark-highlight-mark';
import type { PluggableList } from 'unified';

import { commentList, commentListType } from './Comment.css';

type MarkDownRenderProps = {
  children?: ReactNode;
  className?: string;
};

type MyListProps = ComponentPropsWithoutRef<'div'> & Record<string, unknown>;
type MarkdownListProps = (ComponentPropsWithoutRef<'ul'> | ComponentPropsWithoutRef<'ol'>) & ExtraProps;
type CommentListType = keyof typeof commentListType;

const remarkPlugins: PluggableList = [remarkGfm, remarkHighlightMark];

const components: Components = {
  ul(props) {
    return renderList(props, 'disc');
  },
  ol(props) {
    return renderList(props, 'decimal');
  },
};

export const MyList = ({ children, ...props }: MyListProps) => <div {...props}>{children}</div>;

const MarkDownRender = (props: MarkDownRenderProps) => {
  const markdown = normalizeMarkdown(props.children);

  return (
    <div className={props.className}>
      <Markdown remarkPlugins={remarkPlugins} components={components}>
        {markdown}
      </Markdown>
    </div>
  );
};

function normalizeMarkdown(children: ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(normalizeMarkdown).join('');
  if (children == null || typeof children === 'boolean') return '';
  if (React.isValidElement<{ children?: ReactNode }>(children)) {
    return normalizeMarkdown(children.props.children);
  }

  return '';
}

function renderList(props: MarkdownListProps, type: CommentListType) {
  const { children } = props;

  return <MyList className={classNames(commentList, commentListType[type])}>{children}</MyList>;
}

export default MarkDownRender;
